<script setup lang="ts">
import { Info, Minus, X } from '@lucide/vue'
import { useDebounceFn } from '@vueuse/core'
import { computed, nextTick, ref, watch } from 'vue'

import { Textarea } from '@/components/ui/textarea'
import MarkdownWidgetDisplay from '@ja/core-ui/composites/inline-widgets/MarkdownWidgetDisplay.vue'
import { useDateFormat } from '@ja/core-ui/lib/date'
import { apiFetch, useApiMutation, useApiQuery } from '@ja/portal-query'

import { useCommunicationTranslations } from '../composables/useCommunicationTranslations'
import { useConversationChannel } from '../composables/useConversationChannel'
import { useMessenger } from '../composables/useMessenger'
import type { MessageStatus } from '../types/message'

import TypingIndicator from './TypingIndicator.vue'

interface Message {
  id: string
  content: string
  sender?: { id: string; name: string }
  attachments: { id: string; name: string; url: string }[]
  created_at: string
  is_own: boolean
  status?: MessageStatus
}

const props = defineProps<{
  conversationId: string
  communicationId?: string
  title: string
  recipientName?: string
  userId: string
  minimized: boolean
}>()

const messenger = useMessenger()
const { tm } = useCommunicationTranslations('communication')
const localMessages = ref<Message[]>([])
const messageInput = ref('')
const messagesContainer = ref<HTMLElement | null>(null)
const typingUsers = ref<Set<string>>(new Set())
const showContext = ref(true)

// Fetch initial messages
const { data: initialData, isLoading } = useApiQuery<{ data: Message[] }>(
  computed(() => ['conversation', props.conversationId, 'messages']),
  computed(() => `/api/conversations/${props.conversationId}/messages`),
  {},
  {
    enabled: computed(() => !!props.conversationId && !!props.userId),
    // Real-time updates come from Echo; avoid refetch races overwriting WebSocket-appended messages
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  }
)

// Fetch original communication if available
const { data: communicationData } = useApiQuery<any>(
  computed(() => ['communication', props.communicationId]),
  computed(() => `/api/communications/${props.communicationId}`),
  {},
  {
    enabled: computed(() => !!props.communicationId),
  }
)

watch(initialData, (newData) => {
  if (newData?.data) {
    const server = newData.data.map((m) => ({
      ...m,
      is_own: (m.sender?.id ?? (m as any).user_id) === props.userId,
      status: 'sent' as MessageStatus,
    }))
    const serverIds = new Set(server.map((m) => m.id))
    // Preserve sending/failed placeholders (tempId-based) and any realtime-appended messages
    const fromRealtime = localMessages.value.filter((m) => !serverIds.has(m.id))
    localMessages.value = [...server, ...fromRealtime]
    void scrollToBottom()
  }
}, { immediate: true })

const { sendTypingIndicator } = useConversationChannel(
  () => props.conversationId,
  {
    currentUserId: props.userId,
    onMessageCreated: (event) => {
      // a) Frame matches an in-flight placeholder from this tab — upgrade it in place.
      const pendingIdx = localMessages.value.findIndex(
        (m) => m.status === 'sending' && m.sender?.id === event.senderId && m.content === event.content,
      )
      if (pendingIdx !== -1) {
        localMessages.value[pendingIdx] = {
          id: event.messageId,
          content: event.content,
          sender: { id: event.senderId, name: event.senderName },
          attachments: [],
          created_at: event.timestamp,
          is_own: event.senderId === props.userId,
          status: 'sent',
        }
        void scrollToBottom()
        return
      }

      // b) POST response already upgraded the placeholder — skip duplicate.
      if (localMessages.value.some((m) => m.id === event.messageId)) {
        return
      }

      // c) Fresh message (from another user or same-user other tab) — append.
      localMessages.value.push({
        id: event.messageId,
        content: event.content,
        sender: { id: event.senderId, name: event.senderName },
        attachments: [],
        created_at: event.timestamp,
        is_own: event.senderId === props.userId,
        status: 'sent',
      })
      void scrollToBottom()
    },
    onTyping: (event) => {
      const next = new Set(typingUsers.value)
      if (event.isTyping !== false) {
        next.add(event.userName)
      } else {
        next.delete(event.userName)
      }
      typingUsers.value = next
      setTimeout(() => {
        const cleared = new Set(typingUsers.value)
        cleared.delete(event.userName)
        typingUsers.value = cleared
      }, 3000)
    },
  },
)

const scrollToBottom = async () => {
  await nextTick()
  if (messagesContainer.value) {
    messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
  }
}

const { mutate: sendMessageMutation } = useApiMutation<{ data: Message }, unknown, { content: string; tempId: string }>({
  mutationFn: ({ content }) =>
    apiFetch<{ data: Message }>(`/api/conversations/${props.conversationId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ content }),
    }),
  onSuccess: (data, { tempId }) => {
    const idx = localMessages.value.findIndex((m) => m.id === tempId)
    if (idx !== -1) {
      // POST response arrived first — upgrade placeholder to confirmed message.
      localMessages.value[idx] = { ...data.data, is_own: true, status: 'sent' }
      void scrollToBottom()
    }
    // If idx === -1, the realtime frame already upgraded the placeholder — no-op.
  },
  onError: (_err, { tempId }) => {
    const idx = localMessages.value.findIndex((m) => m.id === tempId)
    if (idx !== -1) {
      localMessages.value[idx] = { ...localMessages.value[idx], status: 'failed' }
    }
  },
})

const sendMessage = () => {
  const content = messageInput.value.trim()
  if (!content) return

  const tempId = crypto.randomUUID()
  localMessages.value.push({
    id: tempId,
    content,
    sender: { id: props.userId, name: '' },
    attachments: [],
    created_at: new Date().toISOString(),
    is_own: true,
    status: 'sending',
  })
  messageInput.value = ''
  void sendTypingIndicator(false)
  void scrollToBottom()

  sendMessageMutation({ content, tempId })
}

const retryMessage = (tempId: string) => {
  const idx = localMessages.value.findIndex((m) => m.id === tempId)
  if (idx === -1) return
  const msg = localMessages.value[idx]
  localMessages.value[idx] = { ...msg, status: 'sending' }
  sendMessageMutation({ content: msg.content, tempId })
}

const debouncedTyping = useDebounceFn(async () => {
  if (messageInput.value.trim()) {
    await sendTypingIndicator(true)
  }
}, 300)

// Trigger typing indicator when message input changes
watch(messageInput, () => {
  void debouncedTyping()
})

const { formatTime } = useDateFormat()
</script>

<template>
  <div
    :class="[
      'flex w-80 flex-col border bg-card shadow-xl transition-all duration-300 rounded-t-lg',
      minimized ? 'h-12' : 'h-[450px]'
    ]"
  >
    <!-- Header -->
    <header
      class="flex cursor-pointer items-center justify-between border-b px-3 py-2 bg-primary text-primary-foreground rounded-t-lg"
      @click="messenger.toggleMinimize(conversationId)"
    >
      <div class="min-w-0 flex-1">
        <p class="truncate text-xs font-semibold">
          {{ title }}
        </p>
        <p v-if="!minimized && recipientName && communicationId" class="truncate text-[10px] opacity-80">
          {{ tm('messages.created_by', { owner: recipientName }) }}
        </p>
      </div>
      <div class="flex items-center gap-1">
        <button
          v-if="!minimized && communicationId"
          class="flex items-center gap-1 rounded bg-white/10 px-1.5 py-0.5 text-[10px] font-medium hover:bg-white/20 transition-colors"
          @click.stop="messenger.viewCommunication(communicationId)"
        >
          <Info class="size-3" />
          <span>{{ tm('inbox.original_message') }}</span>
        </button>
        <button
          class="rounded p-1 hover:bg-white/20"
          @click.stop="messenger.toggleMinimize(conversationId)"
        >
          <Minus class="size-3" />
        </button>
        <button
          class="rounded p-1 hover:bg-white/20"
          @click.stop="messenger.closeChat(conversationId)"
        >
          <X class="size-3" />
        </button>
      </div>
    </header>

    <template v-if="!minimized">
      <!-- Original Communication Context Banner -->
      <div
        v-if="communicationId && communicationData?.data && showContext"
        class="group cursor-pointer border-b bg-primary/5 p-3 transition-all hover:bg-primary/10"
        @click="messenger.viewCommunication(communicationId)"
      >
        <div class="flex items-start justify-between mb-1.5">
          <div class="flex items-center gap-2">
            <div class="rounded-full bg-primary/10 p-1 group-hover:bg-primary/20 transition-colors">
              <Info class="size-3 text-primary" />
            </div>
            <span class="text-[10px] font-bold uppercase tracking-widest text-primary/70">
              {{ tm('inbox.original_message') }}
            </span>
          </div>
          <button
            @click.stop="showContext = false"
            class="rounded-full p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <X class="size-3" />
          </button>
        </div>

        <div class="pl-7">
          <p class="text-xs font-bold line-clamp-1 mb-0.5 group-hover:text-primary transition-colors">
            {{ communicationData.data.title }}
          </p>
          <div class="text-muted-foreground line-clamp-2 text-[11px] leading-relaxed italic">
            <MarkdownWidgetDisplay :content="communicationData.data.content" class="!prose-p:m-0" />
          </div>

          <div class="mt-2 flex items-center gap-1 text-[10px] font-semibold text-primary">
            <span class="underline decoration-primary/30 underline-offset-2">
              {{ tm('inbox.view_all') }}
            </span>
          </div>
        </div>
      </div>

      <!-- Messages -->
      <div
        ref="messagesContainer"
        class="flex-1 overflow-y-auto px-3 py-2 space-y-3"
      >
        <div v-if="isLoading" class="flex h-full items-center justify-center text-xs text-muted-foreground">
          {{ tm('messages.loading_messages') }}
        </div>
        <div v-else-if="localMessages.length === 0" class="flex h-full items-center justify-center text-xs text-muted-foreground">
          {{ tm('messages.no_messages') }}
        </div>
        <div
          v-for="message in localMessages"
          :key="message.id"
          :class="['flex flex-col', message.is_own ? 'items-end' : 'items-start']"
        >
          <div
            :class="[
              'max-w-[85%] rounded-lg px-2 py-1.5 text-xs transition-opacity',
              message.is_own ? 'bg-primary text-primary-foreground' : 'bg-muted',
              message.status === 'sending' ? 'opacity-60' : '',
              message.status === 'failed' ? 'opacity-80 ring-1 ring-destructive' : '',
            ]"
          >
            <p v-if="!message.is_own && message.sender" class="mb-0.5 text-[10px] font-bold opacity-70">
              {{ message.sender.name }}
            </p>
            <MarkdownWidgetDisplay
              :content="message.content"
              class="whitespace-pre-wrap break-words !prose-p:m-0"
            />
            <div class="mt-0.5 flex items-center justify-end gap-1">
              <p class="text-[9px] opacity-60">
                {{ formatTime(message.created_at) }}
              </p>
              <span v-if="message.status === 'sending'" class="text-[9px] opacity-60">
                &middot; {{ tm('messages.sending') }}
              </span>
              <template v-if="message.status === 'failed'">
                <span class="text-[9px] text-destructive">
                  &middot; {{ tm('messages.failed') }}
                </span>
                <button
                  class="text-[9px] text-destructive underline hover:no-underline"
                  @click.stop="retryMessage(message.id)"
                >
                  {{ tm('messages.retry') }}
                </button>
              </template>
            </div>
          </div>
        </div>
        <div class="mt-1">
          <TypingIndicator :typing-users="typingUsers" compact />
        </div>
      </div>

      <!-- Input -->
      <div class="border-t p-2">
        <form class="flex items-end gap-2" @submit.prevent="sendMessage">
          <Textarea
            v-model="messageInput"
            markdown
            compact
            class="flex-1 [&_.ProseMirror]:max-h-[96px] [&_.ProseMirror]:overflow-y-auto"
            :placeholder="tm('messages.type_a_message')"
          />
          <button
            type="submit"
            :disabled="!messageInput.trim()"
            class="rounded-md bg-primary p-1.5 text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="size-3" viewBox="0 0 20 20" fill="currentColor">
              <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
            </svg>
          </button>
        </form>
      </div>
    </template>
  </div>
</template>
